import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { supabase, SUPABASE_URL } from '../services/supabase';
import { initSessionCache, getCachedSession } from '../services/sessionCache';
import { reportWarning } from '../utils/logger';
import { alDenegarLectura } from '../services/sesionPerdida';
import { UserRole, ViewState } from '../types';
import { vistasPorDefectoDeRol } from '../constants/roles';
import type { User, Session } from '@supabase/supabase-js';

interface AuthContextType {
  currentUser: { uid: string; email: string | null; displayName: string | null; photoURL: string | null } | null;
  loading: boolean;
  isAuthorized: boolean;
  isSuperAdmin: boolean;
  userRole: UserRole | null;
  /** allowed_users.id for the signed-in email — stable identity for the app's tenancy layer.
   *  Differs from currentUser.uid (auth.users.id). */
  allowedUserId: string | null;
  clientId: string | null;
  allowedViews: ViewState[];
  login: () => Promise<void>;
  logout: () => Promise<void>;
  retryAuth: () => Promise<void>;
  authError: string | null;
  /**
   * «No se sabe si hay sesión», que NO es lo mismo que «no hay sesión».
   *
   * Se pone a `true` cuando el arranque de auth se acaba sin una respuesta
   * firme —el `getSession()` colgado del candado de gotrue, sobre todo—, y el
   * temporizador de seguridad tiene que bajar el spinner igualmente.
   *
   * Existe porque `loading=false` + `session=null` es indistinguible de haber
   * cerrado sesión, y `App.tsx` respondía a esa combinación con
   * `<Navigate to="/" replace />`: a alguien con sesión VÁLIDA leyendo
   * `/proposal/<id>` lo echaba a la página de marketing y, por el `replace`,
   * ni el botón de atrás lo devolvía. La fila `6c2d43ef` del registro lo
   * documenta: lleva `user_email` y `workspace_id` sacados del JWT por el
   * servidor, o sea que la sesión estaba viva mientras React la tenía en
   * `null`. Con esta bandera, App enseña una pantalla de reintento en la ruta
   * donde está la persona en vez de destruirle el historial.
   */
  authStalled: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Track which emails we've already logged the "not found after retries" path
// for in the current page lifetime. Uninvited users who repeatedly attempt
// Google OAuth would otherwise generate 3-retry × N-attempts log spam.
const _loggedNotFoundEmails = new Set<string>();

/**
 * ¿Queda un token de gotrue guardado en este navegador?
 *
 * Sirve para separar «no hay sesión» de «no se pudo averiguar» cuando lo único
 * que llega es un `null` pelado. Y no es una corazonada: es la MISMA decisión
 * que ya tomó gotrue un momento antes. En `_callRefreshToken`
 * (`@supabase/auth-js`, GoTrueClient.js:1995-2004), si el refresco falla con un
 * error NO reintentable —`refresh_token` revocado, usuario borrado— llama a
 * `_removeSession()`, que borra `sb-<ref>-auth-token` y emite `SIGNED_OUT`; si
 * el error SÍ es reintentable (`AuthRetryableFetchError`: la red) deja la clave
 * donde estaba y se limita a devolver el error.
 *
 * O sea: token guardado + sesión en `null` = el navegador todavía cree que hay
 * con qué volver, y lo que ha fallado es preguntarlo.
 *
 * Se mira la clave de la SESIÓN y sus trozos (`...-auth-token.0`), nunca
 * `-auth-token-user` ni `-auth-token-code-verifier`, que sobreviven a cosas a
 * las que la sesión no.
 */
/**
 * La clave EXACTA en la que gotrue guarda la sesión de ESTE proyecto:
 * `sb-<ref>-auth-token`, donde `<ref>` es el subdominio de la URL de Supabase.
 *
 * Antes se aceptaba cualquier `sb-*-auth-token`. Con eso, una clave huérfana de
 * otro proyecto en el mismo origen —que en `localhost:3008` le pasa a
 * cualquiera que haya apuntado `VITE_SUPABASE_URL` a otro sitio, y también tras
 * un `signOut()` sin red, porque `_signOut` se rinde antes de llamar a
 * `_removeSession()`— dejaba la tarjeta de reintento puesta PARA SIEMPRE: el
 * botón de reintentar mira esta misma función y nunca dejaba de ver un token.
 */
const CLAVE_DE_SESION = (() => {
  try {
    const host = new URL(SUPABASE_URL).hostname;      // <ref>.supabase.co
    return `sb-${host.split('.')[0]}-auth-token`;
  } catch {
    return '';
  }
})();

export const hayTokenGuardado = (): boolean => {
  try {
    const almacen = typeof window === 'undefined' ? null : window.localStorage;
    if (!almacen) return false;
    for (let i = 0; i < almacen.length; i++) {
      const clave = almacen.key(i);
      if (!clave || clave !== CLAVE_DE_SESION) continue;
      if (almacen.getItem(clave)) return true;
    }
  } catch {
    /* Modo privado, almacenamiento desalojado, cookies de terceros cerradas.
       Se devuelve `false` a propósito: sin almacén tampoco hay sesión que
       persistir, así que ahí un `null` sí es un «no hay sesión». */
  }
  return false;
};

/** Plazo para cualquier llamada a gotrue. Todas —`getSession`,
 *  `refreshSession`— pelean por el mismo candado del navegador
 *  (`lock:sb-<ref>-auth-token`) y ninguna trae plazo propio. */
const ESPERA_GOTRUE_MS = 8000;

/** Entre dos comprobaciones de «¿se perdió la sesión por debajo?». Una ráfaga
 *  de lecturas denegadas —quince consultas con tres intentos cada una— es UNA
 *  pregunta, no cuarenta y cinco. */
const ESPERA_ENTRE_COMPROBACIONES_MS = 30_000;

/** Corre `promesa` con plazo. Al agotarse RECHAZA: quien llama decide qué
 *  significa eso, pero lo que no puede es esperar para siempre. */
const conPlazo = <T,>(promesa: PromiseLike<T>, ms: number, queEs: string): Promise<T> => {
  let temporizador: ReturnType<typeof setTimeout>;
  return Promise.race([
    Promise.resolve(promesa).finally(() => clearTimeout(temporizador)),
    new Promise<never>((_, rechazar) => {
      temporizador = setTimeout(() => rechazar(new Error(`${queEs} sin respuesta en ${ms / 1000}s`)), ms);
    }),
  ]);
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authStalled, setAuthStalled] = useState(false);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [allowedUserId, setAllowedUserId] = useState<string | null>(null);
  const [allowedViews, setAllowedViews] = useState<ViewState[]>([]);

  // Mirror of `loading` for the safety-timer closure. setTimeout captures
  // closure values at scheduling time — without this ref, the timer's check
  // `if (loading)` would always read the initial `true` and fire the warning
  // on every page load that stays open >25s, even when auth resolved fine.
  const loadingRef = useRef(true);
  useEffect(() => { loadingRef.current = loading; }, [loading]);

  // ¿Hemos recibido alguna vez una respuesta FIRME sobre si hay sesión?
  // Qué cuenta como firme lo decide `esRespuestaFirme`, más abajo. En una
  // línea: una sesión de verdad, o un `null` con el almacén del navegador
  // igual de vacío. Un `null` con el token todavía guardado no afirma nada.
  const respuestaFirme = useRef(false);

  // El plazo de seguridad se cumplió sin que nadie contestara. Es un PESTILLO:
  // a partir de ahí sólo una sesión de verdad o un `SIGNED_OUT` explícito
  // pueden quitar `authStalled`. Sin él, gotrue deshacía el arreglo él solo:
  // cuando su candado se rinde (a los 30 s, con el temporizador de 25 s ya
  // disparado), `_emitInitialSession` emite `callback('INITIAL_SESSION', null)`
  // DESDE SU `catch` (GoTrueClient.js:1644-1647) — un fallo con la misma cara
  // que un «aquí no hay nadie». La persona veía la pantalla de reintento unos
  // segundos y acababa en la landing igual, con el historial pisado.
  const plazoVencidoSinRespuesta = useRef(false);

  // Espejo de `isAuthorized` para el detector de sesión perdida, que corre
  // desde fuera de React (services/sesionPerdida) y no puede leer el estado.
  const isAuthorizedRef = useRef(false);
  useEffect(() => { isAuthorizedRef.current = isAuthorized; }, [isAuthorized]);

  // Último correo con permisos resueltos EN FIRME. Vivía en un `let` dentro
  // del efecto de arranque; ahora lo necesita también `resolvePermissions`,
  // así que es un ref. Se pone solo cuando la respuesta fue firme (con o sin
  // permisos): tras un fallo se queda en null, para que el siguiente
  // TOKEN_REFRESHED del mismo correo vuelva a preguntar. Antes se ponía
  // también tras el fallo, y un bloqueo se quedaba pegado hasta recargar
  // aunque llegara un token bueno un minuto después.
  const lastResolvedEmail = useRef<string | null>(null);

  // El aviso que sobrevive al SIGNED_OUT que provoca `perderSesion`: sin
  // esto, la limpieza de ese evento borraba «Your session expired…» nada más
  // ponerlo. `login()` y `logout()` lo vacían.
  const avisoSesionPerdida = useRef<string | null>(null);

  /**
   * Deja la app como tras un SIGNED_OUT, sin tocar el almacén de gotrue.
   * La misma limpieza en las tres puertas: el evento SIGNED_OUT, el botón de
   * salir y la sesión perdida por debajo (`perderSesion`).
   */
  const limpiarSesionLocal = () => {
    lastResolvedEmail.current = null;
    respuestaFirme.current = true;
    // Y se suelta el pestillo. Sin esto, `esRespuestaFirme()` sigue
    // cortocircuitando y el PRIMER null que llegue detrás vuelve a levantar
    // la tarjeta de reintento a alguien que acaba de salir a propósito.
    // Es el orden real de gotrue: al revocarse el refresh_token,
    // `_callRefreshToken` llama a `_removeSession()` (emite SIGNED_OUT) y
    // luego `_emitInitialSession` emite `INITIAL_SESSION, null` desde su
    // catch, por la misma `__loadSession` que falló.
    plazoVencidoSinRespuesta.current = false;
    setAuthStalled(false);
    setSession(null);
    setIsAuthorized(false);
    setIsSuperAdmin(false);
    setUserRole(null);
    setAllowedViews([]);
    setClientId(null);
    setAllowedUserId(null);
    setAuthError(avisoSesionPerdida.current);
    setLoading(false);
  };

  /**
   * La sesión se fue POR DEBAJO de la app.
   *
   * Cuando gotrue se queda sin sesión guardada, supabase-js no falla: manda
   * cada petición con la llave publicable en vez del JWT (`fetchWithAuth`:
   * `accessToken ?? supabaseKey`). El servidor contesta 401 / «permission
   * denied», y aquí `isAuthorized` seguía en `true` con el correo en pantalla:
   * la app reintentaba al volver el foco, al reconectar el canal en vivo, al
   * montar cada vista. Un solo navegador dejó 912 informes así entre el 24 y el
   * 30 de agosto de 2026, y ninguna pantalla le dijo nada a la persona.
   *
   * La prueba es la MISMA que usa `esRespuestaFirme`: sin token guardado no hay
   * sesión, diga lo que diga el estado de React. «Reintentar» no puede salir
   * bien —sin token, la RPC vuelve a salir como anon—, así que lo honesto es
   * cerrar la sesión local y mandar a entrar otra vez. El aviso lleva contexto
   * a propósito: la causa de que el almacén quede vacío con la app abierta no
   * se pudo determinar desde los informes, y esto es lo que la va a enseñar.
   */
  const perderSesion = (origen: string, motivo: string) => {
    reportWarning(
      `[Auth] bloqueo: sesión perdida por debajo de la app (${origen}) — ${motivo}. `
      + `Sin token guardado; sesión en caché: ${getCachedSession() ? 'sí' : 'no'}; `
      + `pestaña ${typeof document === 'undefined' ? '?' : document.visibilityState}. Se cierra la sesión local.`,
      { component: 'AuthContext' },
    );
    avisoSesionPerdida.current = 'Your session expired. Please sign in again.';
    limpiarSesionLocal();
    // Que gotrue y las demás pestañas se enteren. Sin esperar y sin que un
    // candado atascado nos retenga: aquí ya está todo limpio.
    supabase.auth.signOut({ scope: 'local' }).catch(() => { /* ya está limpio */ });
  };

  // Resolve the user's permissions using a SECURITY DEFINER RPC (bypasses RLS).
  // Devuelve si la respuesta fue FIRME —con permisos, o sin invitar— o si no se
  // pudo saber (error de base, sesión muerta). `handleAuthUpdate` sólo recuerda
  // el correo en el primer caso.
  const resolvePermissions = async (email: string): Promise<boolean> => {
    const emailLower = email.toLowerCase().trim();

    // Super Admin: completely isolated — only gets the admin panel, no standard app access
    const SUPER_ADMIN_EMAIL = (import.meta.env.VITE_SUPER_ADMIN_EMAIL || 'alexsbpai@gmail.com').toLowerCase().trim();
    
    // Check if they are in the dynamic system_admins table.
    // Phase 6.5: server reads the email from auth.jwt() directly via the
    // no-arg variant — closes the enumeration vector that existed when the
    // SPA could pass any email here. Phase 11.3.b will re-revoke the
    // parameterized is_system_admin(text) variant once this deploys.
    let isDbAdmin = false;
    try {
        const { data } = await supabase.rpc('is_system_admin');
        if (data) isDbAdmin = true;
    } catch (err) {
        console.error('[Auth] Failed to verify system admin status:', err);
    }

    if (emailLower === SUPER_ADMIN_EMAIL || isDbAdmin) {
      console.log('[Auth] Super Admin detected — loading isolated admin panel.');
      setIsSuperAdmin(true);
      setIsAuthorized(true);
      setAuthError(null);
      setUserRole(null);
      setAllowedViews([]);
      setClientId(null);
      setAllowedUserId(null);
      return true;
    }

    setIsSuperAdmin(false);

    // Use RPC call that runs as SECURITY DEFINER — bypasses RLS entirely.
    // Each retry forces a fresh session via getSession() to handle expired tokens.
    const MAX_RETRIES = 3;
    let member: any = null;
    let error: any = null;
    // Un refresco de token, y sólo uno, ante un 42501. Concede además una
    // vuelta extra al bucle para que el reintento quepa aunque la denegación
    // llegue en el último intento.
    let refrescoIntentado = false;
    // La sesión está muerta de verdad: ni siquiera refrescando el token vuelve
    // la RPC. Se trata aparte del `error` genérico de más abajo.
    let sesionMuerta = false;

    try {
      for (let attempt = 1; attempt <= MAX_RETRIES + (refrescoIntentado ? 1 : 0); attempt++) {
        // Retrying the RPC call. We do not need to call getSession() again here
        // because the outer getSession() already ensured the token is fresh.

        // Phase 7: identity now read server-side from auth.jwt() ->> 'email'.
        // The p_user_email parameter is still accepted for backward compat
        // but ignored; the server uses the JWT email exclusively.
        const { data, error: rpcError } = await supabase
          .rpc('get_my_permissions')
          .maybeSingle();

        if (rpcError) {
          error = rpcError;
          // 42501 "permission denied for function" = dead/anon session: gotrue
          // lost the JWT mid-flight, supabase-js fell back to the anon apikey,
          // and the authenticated-only EXECUTE grant correctly denied it.
          // Reintentar la RPC tal cual no puede salir bien sin un token válido
          // — pero conseguir un token válido es lo que hace `refreshSession()`,
          // y eso es lo que se intenta aquí abajo antes de rendirse. Las notas
          // van a console.info para que la denegación esperada no inunde el
          // registro; el bloqueo de verdad se reporta aparte, una sola vez.
          if (rpcError.code === '42501' || /permission denied for function/i.test(rpcError.message || '')) {
            // Aquí había un `break` seco. El control caía en el bloque
            // `if (error)` de abajo, que apaga `isAuthorized` y pinta
            // «Database Error: permission denied for function …»: un tropiezo
            // de permisos echaba de la app a alguien con sesión válida, y con
            // jerga de Postgres por toda explicación.
            //
            // Es verdad que reintentar la RPC tal cual no puede salir bien sin
            // un token válido — pero repartir un token válido es exactamente
            // lo que hace `refreshSession()`. Se intenta UNA vez; si repone la
            // sesión, el bucle sigue y la persona no se entera de nada.
            if (!refrescoIntentado) {
              refrescoIntentado = true;
              console.info('[Auth] RPC denegada (sesión anon o token perdido): se refresca el token y se reintenta —', rpcError.message);
              try {
                // Con plazo: `refreshSession()` toma el mismo candado de
                // gotrue que el resto de esta unidad, y sin plazo un candado
                // atascado dejaba a la persona mirando el spinner hasta los
                // 25 s del temporizador de seguridad para acabar igualmente en
                // «acceso denegado». Que falle a los 8 s es mejor que eso.
                const { data: refrescada, error: errRefresco } = await conPlazo(
                  supabase.auth.refreshSession(),
                  ESPERA_GOTRUE_MS,
                  'refreshSession',
                );
                if (!errRefresco && refrescada?.session) {
                  error = null;
                  continue;
                }
                if (errRefresco) console.info('[Auth] refreshSession no repuso la sesión:', errRefresco.message);
              } catch (errRefresco: any) {
                console.info('[Auth] refreshSession lanzó:', errRefresco?.message || errRefresco);
              }
            }
            sesionMuerta = true;
            break;
          }
          console.warn(`[Auth] RPC error on attempt ${attempt}/${MAX_RETRIES}:`, rpcError.message);
        } else if (data) {
          member = data;
          error = null;
          break;
        } else if (!_loggedNotFoundEmails.has(emailLower)) {
          // Only log the per-retry breadcrumb the FIRST time we see this email.
          // Repeat sign-in attempts by an uninvited user shouldn't multiply logs.
          console.log(`[Auth] No match on attempt ${attempt}/${MAX_RETRIES}, retrying in ${attempt}s...`);
        }

        if (attempt < MAX_RETRIES) {
          await new Promise(r => setTimeout(r, attempt * 1000));
        }
      }
    } catch (err: any) {
      console.error('[Auth] Unexpected exception in resolvePermissions:', err);
      error = { message: err.message, code: 'EXCEPTION' };
    }

    if (sesionMuerta) {
      if (!hayTokenGuardado()) {
        // No es un tropiezo: gotrue ya no tiene NADA guardado y cada petición
        // sale con la llave publicable. La tarjeta de «Try Again» de abajo era
        // un callejón sin salida —el botón llama a esta misma función y vuelve
        // a salir como anon—. Ver `perderSesion`.
        perderSesion('get_my_permissions', error?.message || 'sin mensaje');
        return false;
      }
      // Con token guardado sí puede ser un tropiezo (un refresco a medias, la
      // red): se conserva la sesión y se ofrece reintentar.
      //
      // Se reporta con CADENA SUELTA a propósito: el filtro de ruido del logger
      // descarta todo lo que traiga `"code":"42501"` serializado dentro, así que
      // pasando el objeto crudo este bloqueo seguiría sin verse en el panel.
      reportWarning(
        `[Auth] bloqueo: la RPC de permisos sigue denegada tras refrescar el token — ${error?.message || 'sin mensaje'}`,
        { component: 'AuthContext' },
      );
      setIsAuthorized(false);
      // Mensaje humano en vez de «Database Error: permission denied for
      // function get_my_permissions». La pantalla de acceso denegado ya trae
      // un botón de Reintentar, que ahora sí puede salir bien.
      setAuthError('Your session expired. Press Try Again, or sign out and sign back in.');
      setUserRole(null);
      setAllowedViews([]);
      setClientId(null);
      setAllowedUserId(null);
      return false;
    }

    if (error) {
      console.error('[Auth] Database error checking permissions:', error);
      setIsAuthorized(false);
      setAuthError(`Database Error: ${error.message}${error.code ? ` (Code: ${error.code})` : ''}`);
      setUserRole(null);
      setAllowedViews([]);
      setClientId(null);
      setAllowedUserId(null);
      return false;
    }

    if (!member) {
      // Not a bug — uninvited Google sign-ins. The user already gets the
      // friendly authError message below. Use console.info (not warn/error)
      // so it stays visible in the dev console for debugging onboarding
      // flows but doesn't pollute the admin error logger.
      // Dedupe per-session so a single uninvited user retrying 5 times
      // doesn't produce 5 identical log lines.
      if (!_loggedNotFoundEmails.has(emailLower)) {
        _loggedNotFoundEmails.add(emailLower);
        console.info('[Auth] User not found after retries:', emailLower);
      }
      setIsAuthorized(false);
      setAuthError('Your account has not been invited. Ask an Admin to add your email in the Workforce tab.');
      setUserRole(null);
      setAllowedViews([]);
      setClientId(null);
      setAllowedUserId(null);
      // Firme: la base contestó y dijo que no. Reintentar lo hace el botón.
      return true;
    }

    const role = (member.role as UserRole) || 'Laborer';
    const views: ViewState[] = role === 'Admin'
      // NOTE: any new ViewState must be added here or an Admin clicking it in
      // the sidebar gets silently redirected — Sidebar bypasses the allow-list
      // for Admins, but the NavigationContext route guard does not.
      ? ['Dashboard', 'Leads', 'Clients', 'Estimates', 'Projects', 'Finance', 'Calendar', 'Pricebook', 'Templates', 'ProposalViewer', 'Settings', 'FollowUps', 'Services', 'TimeClock', 'Workforce', 'Inventory', 'Classes']
      : (member.allowed_views && member.allowed_views.length > 0
          ? member.allowed_views
          /* Lista vacía: se deriva del ROL en vez de dejarla en blanco.
             En producción 176 de 196 filas tienen `allowed_views` vacía. Los
             Admin no importan (arriba), pero quedan 7 personas —capataces,
             mantenimiento y oficina— cuya lista está en blanco. Con la lista
             vacía el guardia de rutas se apagaba entero y por eso podían
             trabajar... y también alcanzar /finance y /materials. Al cerrar
             ese agujero, dejarlas en blanco las habría encerrado en el reloj
             con el menú vacío. Derivar del rol cierra el agujero sin echar a
             nadie. Ver constants/roles.ts. */
          : vistasPorDefectoDeRol(role));

    setIsAuthorized(true);
    setAuthError(null);
    setUserRole(role);
    setAllowedViews(views);
    setClientId(member.client_id || null);
    setAllowedUserId(member.user_id || null);
    return true;
  };

  // Bootstrap: check for existing session on mount
  useEffect(() => {
    let isMounted = true;

    // ── Expired-OAuth redirect handling ───────────────────────────────
    // When a user lands on /?error=...&error_code=bad_oauth_state (or
    // #error_code=otp_expired in the hash for magic-link flows), supabase-js
    // rejects internally and never emits an auth event — the safety timer
    // below then fires after 25s. Detect and clean these URLs up front:
    //   - strip the error params so reloads don't replay the failure
    //   - drop the spinner immediately (no point waiting 25s)
    //   - the LoginView will surface the situation via authError
    const checkOAuthErrorRedirect = (): { hadError: boolean; code: string | null } => {
      if (typeof window === 'undefined') return { hadError: false, code: null };
      const search = new URLSearchParams(window.location.search);
      const hash = new URLSearchParams(window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '');
      const code = search.get('error_code') || hash.get('error_code');
      const errType = search.get('error') || hash.get('error');
      if (!code && !errType) return { hadError: false, code: null };
      // Strip the redirect params so the user can retry without replaying the error
      try {
        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      } catch { /* ignore */ }
      return { hadError: true, code: code || errType };
    };

    const oauthErr = checkOAuthErrorRedirect();
    if (oauthErr.hadError) {
      console.info(`[Auth] OAuth redirect carried error (${oauthErr.code}); cleaning URL and surfacing retry CTA.`);
      setAuthError('Your sign-in link expired before completing. Please sign in again.');
    }

    // Safety timeout — if auth never resolves within 25 seconds, drop the spinner.
    // Reads from loadingRef (not the closure-stale `loading`) so it only fires
    // when auth genuinely hung. Without the ref, the closure captures the
    // initial `loading=true` and the warning fires on every page open >25s
    // regardless of actual auth state.
    //
    // If the page was loaded with an OAuth error in the URL, supabase-js will
    // never finish bootstrap — log at info (not warn) so this expected case
    // doesn't get surfaced in the admin error panel as a "warning" cluster.
    const safetyTimer = setTimeout(() => {
      if (isMounted && loadingRef.current) {
        if (oauthErr.hadError) {
          console.info('[Auth] Safety timeout after OAuth error redirect — forcing loading=false');
        } else if (!respuestaFirme.current) {
          // Se acabó el tiempo sin una respuesta firme. Bajamos el spinner
          // —la app no puede quedarse colgada— pero dejamos dicho que esto es
          // «no se sabe», no «no hay sesión», para que App.tsx enseñe un
          // reintento en vez de mandar a la landing con el historial pisado.
          //
          // Va a `console.error` (no `warn`) y con CADENA SUELTA a propósito:
          // `consolePatch` lo convierte en un informe, y el objeto crudo de un
          // PostgrestError trae `"code":"42501"` dentro, que el filtro de ruido
          // del logger descarta. Un bloqueo real tiene que verse.
          plazoVencidoSinRespuesta.current = true;
          setAuthStalled(true);
          console.error(`[Auth] la sesión no se resolvió en 25s — estado desconocido, no se redirige (sesión en caché: ${getCachedSession() ? 'sí' : 'no'})`);
        } else {
          console.warn('[Auth] Safety timeout — forcing loading=false');
        }
        setLoading(false);
      }
    }, oauthErr.hadError ? 2000 : 25000);

    let activeResolutionPromise: Promise<void> | null = null;

    /**
     * ¿Esta respuesta AFIRMA que no hay sesión, o sólo dice que no se pudo
     * averiguar?
     *
     * Antes se decidía por la FORMA: o venía una sesión, o el nombre del evento
     * estaba en una lista. No basta, y lo que se colaba por ahí es el caso más
     * común de todos. `GoTrueClient.__loadSession` (GoTrueClient.js:1241-1244)
     * devuelve `{ data: { session: null }, error }` cuando el token guardado ha
     * caducado y `_callRefreshToken` no consigue renovarlo: alguien
     * AUTENTICADO, con su `refresh_token` perfectamente bueno, en una conexión
     * mala o sin conexión. `services/sessionCache.ts` se queda sólo con
     * `data.session` y tira el `error`, así que aquí llegaba un `null` pelado,
     * indistinguible del de quien no ha entrado nunca — y de ahí a
     * `<Navigate to="/" replace />`.
     *
     * `INITIAL_SESSION` con `null` tampoco vale como afirmación: gotrue lo
     * emite DESDE EL `catch` de `_emitInitialSession`, o sea que cualquier
     * fallo durante la recuperación llega con esa misma cara.
     *
     * La pregunta que sí se puede contestar sin ver el `error` es la del
     * navegador: ¿queda un token guardado? Ver `hayTokenGuardado()` arriba —
     * gotrue borra la clave cuando la sesión está muerta de verdad y la
     * conserva cuando lo que falló fue la red.
     *
     * `SIGNED_OUT` no aparece aquí porque tiene su propia rama en
     * `onAuthStateChange`, que limpia `authStalled` Y suelta el pestillo
     * `plazoVencidoSinRespuesta` — las dos mitades, o el siguiente null
     * volvería a levantar la tarjeta.
     */
    const esRespuestaFirme = (currentSession: Session | null): boolean => {
      if (currentSession) return true;
      // Con el pestillo echado, un `null` no vale para nada: ver
      // `plazoVencidoSinRespuesta`.
      if (plazoVencidoSinRespuesta.current) return false;
      return !hayTokenGuardado();
    };

    const handleAuthUpdate = async (currentSession: Session | null, source: string) => {
      console.log(`[Auth] Handling update from ${source}`);
      if (esRespuestaFirme(currentSession)) {
        respuestaFirme.current = true;
        if (isMounted) setAuthStalled(false);
      } else if (isMounted) {
        // Un `null` que no afirma nada. No se espera a los 25 s del
        // temporizador: se marca ya, y App enseña el reintento en la ruta
        // donde está la persona en vez de mandarla a la landing.
        setAuthStalled(true);
      }
      if (isMounted) setSession(currentSession);

      if (!currentSession) {
        if (isMounted) setLoading(false);
        return;
      }

      if (currentSession.user?.email) {
        const email = currentSession.user.email;

        // Skip if already resolved
        if (lastResolvedEmail.current === email) {
          if (isMounted) setLoading(false);
          return;
        }

        // Wait if currently resolving
        if (activeResolutionPromise) {
          await activeResolutionPromise;
          if (isMounted) setLoading(false);
          return;
        }

        // Start new resolution. El correo sólo se recuerda si la respuesta
        // fue firme: tras un fallo se vuelve a preguntar con el próximo evento.
        console.log(`[Auth] Resolving permissions for ${email} (source: ${source})`);
        activeResolutionPromise = resolvePermissions(email)
          .then((firme) => { lastResolvedEmail.current = firme ? email : null; })
          .finally(() => {
            activeResolutionPromise = null;
          });

        await activeResolutionPromise;
      }

      if (isMounted) setLoading(false);
    };

    // ── El detector de sesión perdida ────────────────────────────────────────
    // Las lecturas denegadas (services/vigilarLecturas) y la lista de talleres
    // vacía (WorkspaceContext) avisan aquí. Con token guardado no se concluye
    // nada —puede ser RLS haciendo su trabajo o un refresco pendiente— y se
    // deja a gotrue; sin token, la sesión se fue por debajo. Ver `perderSesion`.
    let ultimaComprobacion = 0;
    alDenegarLectura((origen, motivo) => {
      if (!isMounted || !isAuthorizedRef.current) return;
      const ahora = Date.now();
      if (ahora - ultimaComprobacion < ESPERA_ENTRE_COMPROBACIONES_MS) return;
      ultimaComprobacion = ahora;
      if (hayTokenGuardado()) {
        console.info(`[Auth] lectura denegada con token guardado (${origen}); se deja a gotrue —`, motivo);
        return;
      }
      perderSesion(origen, motivo);
    });

    // Initial explicit getSession to load token from localStorage. Routed
    // through initSessionCache so this single call also seeds the
    // process-wide session cache that database.ts / logger.ts / tts use,
    // eliminating per-request `getSession()` calls that previously caused
    // gotrue lock contention ("Lock broken by 'steal'" cascade).
    // `initSessionCache()` puede quedarse colgada: por dentro es un
    // `supabase.auth.getSession()`, que pelea por el candado de gotrue y no
    // lleva plazo. Se le pone uno de 8 s; si para entonces `onAuthStateChange`
    // ya dejó una sesión en la caché, se sigue con ella en vez de esperar los
    // 25 s del temporizador de seguridad.
    let arranqueResuelto = false;
    const temporizadorArranque = setTimeout(() => {
      if (!isMounted || arranqueResuelto) return;
      const enCache = getCachedSession();
      if (enCache) {
        console.warn('[Auth] getSession sin responder en 8s — se sigue con la sesión de la caché');
        handleAuthUpdate(enCache, 'sessionCache');
      }
      // Si tampoco hay nada en caché no se concluye NADA: sin respuesta firme,
      // el temporizador de seguridad de abajo marcará `authStalled`.
    }, ESPERA_GOTRUE_MS);

    initSessionCache().then((existingSession) => {
      arranqueResuelto = true;
      clearTimeout(temporizadorArranque);
      handleAuthUpdate(existingSession, 'getSession');
    }).catch((error) => {
      /* OJO, y que no engañe: hoy esta rama NO se dispara cuando `getSession()`
         falla. `services/sessionCache.ts` (:47-55) envuelve la llamada en su
         propio `.catch(() => { bootstrapPromise = null; return null; })`, así
         que `initSessionCache()` no rechaza nunca: el `AbortError: Lock broken
         by another request with the 'steal' option` —que la cabecera de ese
         mismo fichero llama la mayor fuente de ruido del registro— sale por el
         `.then` de arriba como un `null` pelado. Quien salva ese caso es
         `esRespuestaFirme`, mirando si queda un token guardado, NO este
         `.catch`.

         Se queda por dos razones: atrapa que reviente el propio
         `handleAuthUpdate`, y el día que `sessionCache` deje de tragarse el
         error —lo suyo sería que devolviera `{ session, error }` en vez de un
         `Session | null`— esta rama pasa a ser la buena sin tocar nada. */
      arranqueResuelto = true;
      clearTimeout(temporizadorArranque);
      console.error('[Auth] getSession error:', error?.message || error);
      const enCache = getCachedSession();
      if (enCache) handleAuthUpdate(enCache, 'sessionCache');
    });

    // 2. Auth state listeners
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (event === 'SIGNED_OUT') {
        // La misma limpieza que el botón de salir y que `perderSesion`; ese
        // último deja su aviso en `avisoSesionPerdida` para que esto no lo borre.
        limpiarSesionLocal();
        return;
      }

      // Handle sign-ins and token refreshes. INITIAL_SESSION almost always
      // fires for the same email we just resolved via the bootstrap getSession
      // call above — suppress the duplicate to avoid a redundant permissions
      // RPC and another gotrue lock grab.
      if (event === 'INITIAL_SESSION' && newSession?.user?.email === lastResolvedEmail.current) {
        return;
      }
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
        handleAuthUpdate(newSession, event);
      }
    });

    return () => {
      isMounted = false;
      alDenegarLectura(null);
      clearTimeout(safetyTimer);
      clearTimeout(temporizadorArranque);
      subscription.unsubscribe();
    };
  }, []);

  // Map Supabase user to our app's expected shape
  const currentUser = session?.user
    ? {
      uid: session.user.id,
      email: session.user.email ?? null,
      displayName: session.user.user_metadata?.full_name ?? session.user.user_metadata?.name ?? null,
      photoURL: session.user.user_metadata?.avatar_url ?? null,
    }
    : null;

  const login = async () => {
    avisoSesionPerdida.current = null;
    setAuthError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
      },
    });
    if (error) {
      console.error('[Auth] Google login error:', error);
      setAuthError(error.message);
    }
  };

  const logout = async () => {
    avisoSesionPerdida.current = null;   // salir a propósito no deja aviso
    await supabase.auth.signOut();
    limpiarSesionLocal();
  };

  // Allow the UI to manually retry permission resolution (e.g. from "Access Denied" screen)
  const retryAuth = async () => {
    setLoading(true);
    setAuthError(null);

    if (session?.user?.email) {
      await resolvePermissions(session.user.email);
      setLoading(false);
      return;
    }

    // Sin sesión en el estado de React. Antes se salía sin hacer nada, que es
    // lo peor posible en el caso de `authStalled`: ahí puede haber sesión de
    // sobra y lo único que pasó es que el arranque se quedó colgado. Se vuelve
    // a preguntar.
    try {
      // Con plazo: `getSession()` es justo la llamada que se puede quedar
      // colgada del candado de gotrue, y un botón de reintentar que no
      // responde nunca es peor que no tenerlo.
      const enCache = getCachedSession();
      const recuperada = enCache ?? (await conPlazo(
        supabase.auth.getSession().then(({ data }) => data.session),
        ESPERA_GOTRUE_MS,
        'getSession',
      ));
      if (recuperada?.user?.email) {
        plazoVencidoSinRespuesta.current = false;
        respuestaFirme.current = true;
        setAuthStalled(false);
        setSession(recuperada);
        await resolvePermissions(recuperada.user.email);
      } else if (!hayTokenGuardado()) {
        // Hubo respuesta, dice que no hay sesión, y el navegador tampoco
        // guarda ya un token: eso sí es firme. Se quita la bandera —incluido
        // el pestillo— y App puede mandar a la landing con razón.
        plazoVencidoSinRespuesta.current = false;
        respuestaFirme.current = true;
        setAuthStalled(false);
      } else {
        // Ninguna sesión y sin embargo el token sigue guardado: lo que ha
        // fallado es el refresco, no la sesión. Se sigue sin saber, así que la
        // bandera se queda y la persona conserva su ruta y su historial.
        console.warn('[Auth] retryAuth: sin sesión pero con token guardado — se mantiene «no se sabe»');
      }
    } catch (err: any) {
      // Volvió a fallar la pregunta: se sigue sin saber, así que la bandera se
      // queda puesta y la persona conserva su ruta y su historial.
      console.warn('[Auth] retryAuth: no se pudo recuperar la sesión:', err?.message || err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ currentUser, loading, isAuthorized, isSuperAdmin, userRole, allowedUserId, clientId, allowedViews, login, logout, retryAuth, authError, authStalled }}>
      {children}
    </AuthContext.Provider>
  );
};